import React, { ChangeEventHandler, useMemo } from 'react';
import { useSubId } from '../../hooks/useSubId.hook';
import { Form } from 'Common/forms';
import Input from '@material-ui/core/Input';
import { makeStyles } from '@material-ui/core/styles';
import get from 'lodash/get';

import { SomeObject } from 'Types/common.types';
import InfoTooltip from '../../components/common/InfoTooltip/InfoTooltip';

import { TestID } from 'src/testID';
import { calculateSlaPrice, toPriceFn, decodeEncodedPrice } from 'src/utils/priceCalculator.utils';
import type { PriceEnvironment } from 'src/types/subscription.types';

import './style.css';

function fixedType(key: string) {
  switch (key) {
    case 'datahub':
      return 'Datahub';
    case 'microservices':
      return 'Microservices';
    default:
      return key;
  }
}

function dynamicType(key: string) {
  switch (key) {
    case 'backup_pr_GB':
      return 'Backup';
    case 'sla_pr_GB':
      return 'Service level';
    case 'storage_pr_GB':
      return 'Base data price';
    case 'vpn_pr_GB':
      return 'VPN';
    default:
      return key;
  }
}

type PriceCalculatorProps = {
  value: {
    currency: string;
    fixed_total: string;
    dynamic_total: string;
    expected_total_data_cost?: string;
    fixed_items: {
      [key: string]: {
        sub_type: PriceEnvironment;
        price: string;
      };
    };
    dynamic_items: {
      [key: string]: {
        price: string;
      };
    };
  };
  oldPrice: SomeObject;
  shouldFetchNotificationRules: boolean;
  expectedDataSize: number;
  onExpectedDataSizeChange: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  disableExpectedDataSize: boolean;
  currentDataSize: number;
  products?: SomeObject;
  isProvisionerVersion2?: boolean;
};

const useStyles = makeStyles({
  input: {
    justifyContent: 'flex-end',
  },
});

const getDataSize = (
  subType: PriceEnvironment,
  dataSize: number,
  isSearchEnabled: boolean,
  isProvisionerVersion2: boolean | undefined
) => {
  if (subType === 'developer' || subType === 'developer-pro') {
    return 0;
  }

  // Integrated search incures a 33% increase in expected data size
  return isSearchEnabled && isProvisionerVersion2 ? dataSize + dataSize * 0.33 : dataSize;
};

export const PriceCalculator: React.FC<React.PropsWithChildren<PriceCalculatorProps>> = (props) => {
  const subId = useSubId();
  const styles = useStyles();
  const toPrice = useMemo(() => toPriceFn(props.value.currency), [toPriceFn, props.value.currency]);

  const subType = props.value.fixed_items.datahub.sub_type;
  const isSearchEnabled = get(props, 'products.search');
  const expectedDataSize = getDataSize(
    subType,
    props.expectedDataSize,
    isSearchEnabled,
    props.isProvisionerVersion2
  );
  const currentDataSize = getDataSize(
    subType,
    props.currentDataSize,
    isSearchEnabled,
    props.isProvisionerVersion2
  );

  const enhancedPrice = useMemo(
    () => ({
      dynamicTotal: decodeEncodedPrice(props.value.dynamic_total),
      monitoringPerGB: decodeEncodedPrice(props.value.dynamic_items?.monitoring_pr_GB?.price),
      expectedTotalDataCost: decodeEncodedPrice(props.value.expected_total_data_cost),
      slaPerGB: decodeEncodedPrice(props.value.dynamic_items?.sla_pr_GB?.price),
      vpnPerGB: decodeEncodedPrice(props.value.dynamic_items?.vpn_pr_GB?.price),
    }),
    [
      props.value.dynamic_total,
      props.value.dynamic_items?.monitoring_pr_GB?.price,
      props.value.expected_total_data_cost,
      props.value.dynamic_items?.sla_pr_GB?.price,
      props.value.dynamic_items?.vpn_pr_GB?.price,
    ]
  );

  const getServiceLevelCost = () => {
    let size = expectedDataSize;

    if (expectedDataSize < 50) {
      size = 50;
    } else if (expectedDataSize > 300) {
      size = 300;
    }

    return `${size} * ${toPrice(enhancedPrice.slaPerGB.toString())} = ${toPrice(
      slaPrice.toString()
    )}`;
  };

  // Filter out the base data price if subscription size is developer or developer-pro
  const dynamicItemKeys = Object.keys(props.value.dynamic_items).filter(
    (key) => !(key === 'storage_pr_GB' && (subType === 'developer' || subType === 'developer-pro'))
  );

  const isProdEnvironments = subType !== 'developer' && subType !== 'developer-pro';

  const searchPrice =
    isSearchEnabled && props.isProvisionerVersion2 && isProdEnvironments
      ? props.expectedDataSize * 0.33 * enhancedPrice.dynamicTotal
      : 0;

  const slaPrice = calculateSlaPrice(enhancedPrice.slaPerGB, expectedDataSize);

  const vpnPrice = enhancedPrice.vpnPerGB * expectedDataSize;

  return (
    <div className="price-calculator">
      <table>
        <tbody>
          {Object.keys(props.value.fixed_items).map((key) => (
            <tr key={key}>
              <td data-testid={TestID.FixedTypePriceTitleLabel}>
                {fixedType(key)} {/*{props.value.fixed_items[key].sub_type}*/}
              </td>
              <td data-testid={TestID.FixedTypePriceLabel}>
                {' '}
                {toPrice(props.value.fixed_items[key].price)}
              </td>
            </tr>
          ))}

          {isProdEnvironments &&
            dynamicItemKeys.map((key) => (
              <>
                <tr key={key}>
                  {dynamicType(key) !== 'Monitoring' && <td>{dynamicType(key)}</td>}
                  <td> {toPrice(props.value.dynamic_items[key].price)} per GB</td>
                </tr>
                {dynamicType(key) === 'Service level' && (
                  <tr
                    style={{
                      fontSize: '0.8rem',
                      fontWeight: '400',
                      position: 'relative',
                      top: '-10px',
                    }}
                  >
                    <td>
                      Service level cost{' '}
                      <InfoTooltip
                        info="SLA is billed for minimum 50GB data and a maximum of 300GB"
                        style={{ marginLeft: '10px', position: 'relative', bottom: '2px' }}
                      />
                    </td>
                    <td>{getServiceLevelCost()}</td>
                  </tr>
                )}
              </>
            ))}

          {isProdEnvironments && !props.disableExpectedDataSize && (
            <tr>
              <td>Expected data size</td>
              <td>
                <Form component="div" horizontal className={styles.input}>
                  <Input
                    id="expectedDataSize"
                    name="expectedDataSize"
                    value={props.expectedDataSize}
                    margin="dense"
                    onChange={props.onExpectedDataSizeChange}
                    inputProps={{
                      step: 1,
                      min: 0,
                      max: 1000,
                      type: 'number',
                      'aria-labelledby': 'input-slider',
                    }}
                    data-testid={TestID.PriceCalculatorExpectedDataSizeInput}
                  />
                  <label htmlFor="expectedDataSize">GB</label>
                </Form>
              </td>
            </tr>
          )}

          {isProdEnvironments && props.disableExpectedDataSize && (
            <tr>
              <td>Current data size</td>
              <td>{currentDataSize} GB</td>
            </tr>
          )}

          {isProdEnvironments &&
            props.value.expected_total_data_cost &&
            enhancedPrice.expectedTotalDataCost > 0 && (
              <tr>
                <td>Expected data cost</td>
                <td>{toPrice(props.value.expected_total_data_cost)}</td>
              </tr>
            )}
        </tbody>
        <tfoot>
          <tr>
            <td data-testid={TestID.PriceCalculatorTotalPriceTitleLabel}>Total (monthly)</td>
            <td data-testid={TestID.PriceCalculatorTotalPriceLabel}>
              {toPrice(
                props.value.fixed_total,
                enhancedPrice.expectedTotalDataCost,
                vpnPrice,
                searchPrice,
                slaPrice
              )}
              <br />
              {isProdEnvironments &&
                props.expectedDataSize < 1 &&
                `plus ${toPrice(props.value.dynamic_total)} per GB`}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};
